import { useTrackView } from '@/lib/analytics';
import {
  ANNUAL_LABEL,
  BILLING_INTERVALS,
  PLAN_META,
  PLANS,
  annualSavings,
  planPrice,
  TRIAL_REEL,
  reelPrice,
  type BillingInterval,
  type PlanKey,
} from '@str/shared';

/** The one reel price: a trial-length (15 s) reel, as it is charged. */
const REEL = reelPrice(TRIAL_REEL.durationSeconds);
/** About how many reels a month's credits make. */
const reelsAMonth = (credits: number) => Math.floor(credits / REEL.total);

const INTERVAL_LABEL: Record<BillingInterval, string> = { year: 'Annual', month: 'Monthly' };

/** Dollars to the cent, without trailing .00 on whole amounts. */
function dollars(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

/**
 * Plan rows plus the billing-period toggle. Both periods read per month in the same unit; the
 * annual row also states the real charge and what it saves against twelve monthly payments.
 */
export function PlanPicker({
  value,
  onChange,
  interval,
  onIntervalChange,
  billedWhen = 'today',
  intervalLocked = false,
}: {
  value: PlanKey;
  onChange: (p: PlanKey) => void;
  interval: BillingInterval;
  onIntervalChange: (i: BillingInterval) => void;
  /** When the annual amount is charged: "at day 7", "at renewal", "today". */
  billedWhen?: string;
  /** The period follows the existing subscription and cannot change here. */
  intervalLocked?: boolean;
}) {
  useTrackView('plan_picker_viewed', { plan: value, interval, interval_locked: intervalLocked });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
      >
        {intervalLocked ? (
          <span style={{ fontSize: 12.5, color: '#777' }}>
            Your account bills {interval === 'year' ? 'annually' : 'monthly'}; added properties use
            the same billing period.
          </span>
        ) : (
          <div className="seg round" role="radiogroup" aria-label="Billing period">
            {BILLING_INTERVALS.map((i) => (
              <button
                key={i}
                type="button"
                role="radio"
                aria-checked={interval === i}
                className={`seg-item hv${interval === i ? ' on' : ''}`}
                onClick={() => onIntervalChange(i)}
              >
                {INTERVAL_LABEL[i]}
              </button>
            ))}
          </div>
        )}
        <span style={{ fontSize: 11, color: '#999' }}>Prices in USD, per property</span>
      </div>
      {PLANS.map((k) => {
        const p = PLAN_META[k];
        const price = planPrice(k, interval);
        const saving = annualSavings(k);
        const on = value === k;
        return (
          <button
            key={k}
            type="button"
            className={`plan-row hv${on ? ' on' : ''}`}
            onClick={() => onChange(k)}
            aria-pressed={on}
          >
            <span className="radio">
              <i />
            </span>
            <span
              style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {p.name}
                {p.tag && (
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      padding: '2px 7px',
                      borderRadius: 99,
                      whiteSpace: 'nowrap',
                      background: 'var(--accent-tint)',
                      color: 'var(--accent)',
                    }}
                  >
                    {p.tag}
                  </span>
                )}
              </span>
              <span style={{ fontSize: 12, color: '#777', lineHeight: 1.35 }}>
                {p.summary} · about {reelsAMonth(p.creditsPerMonth)}{' '}
                {reelsAMonth(p.creditsPerMonth) === 1 ? 'reel' : 'reels'} a month
              </span>
            </span>
            <span
              className="plan-price"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 1,
                textAlign: 'right',
                maxWidth: '55%',
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                  fontSize: 15,
                  fontWeight: 800,
                  letterSpacing: '-.02em',
                }}
              >
                {interval === 'year' && (
                  <s style={{ fontSize: 12.5, color: '#999', fontWeight: 600 }}>
                    {dollars(p.priceCents)}
                  </s>
                )}
                <span>{dollars(price.perMonthCents)}</span>
                <span style={{ fontSize: 12, color: '#999', fontWeight: 500 }}>/mo</span>
                {interval === 'year' && <span className="plan-save">Save {saving.percent}%</span>}
              </span>
              <span style={{ fontSize: 11, color: '#999', fontWeight: 500 }}>
                {interval === 'year'
                  ? `${dollars(price.totalCents)} billed ${billedWhen} · ${dollars(saving.cents)} less than monthly`
                  : 'billed monthly'}
              </span>
            </span>
          </button>
        );
      })}
      <span style={{ fontSize: 11.5, color: '#777', lineHeight: 1.4 }}>
        What a credit buys: a reel is {REEL.total} credits (storyboard {REEL.storyboard}, filming{' '}
        {REEL.filming}), each new picture or carousel card 1 to 3. Packs of 20 for $10 never expire.
      </span>
      {interval === 'year' && (
        <span style={{ fontSize: 11.5, color: '#777', lineHeight: 1.4 }}>{ANNUAL_LABEL}</span>
      )}
    </div>
  );
}
